import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { NoResult, Loader } from '../../misc/loader-utils';

import { ordersService } from './orders-service';

import Card from './orders-card';
import BasicDropdown from '../../base/dropdowns/basic-dropdown';
import MocksButton from '../../misc/mocks-button';
import ConfirmDropdown from '../rates/confirm-dropdown';

import './orders.scss';

const mockData = ordersService.getMockData();
mockData.attributed_checkouts.forEach(co => {
  co.event_products = ordersService.getMockProductsData();
});

const sortOptions = [
  { label: 'Newest to Oldest', value: 'desc' },
  { label: 'Oldest to Newest', value: 'asc' },
];

const perPage = 10;

const Orders = ({ match }) => {
  const [data, setData] = useState([]);
  const [showMocks, setShowMocks] = useState(false);
  const [pages, setPages] = useState(1);
  const [merchants, setMerchants] = useState([]);
  const [sortOption, setSortOption] = useState(sortOptions[0]);
  const [didMount, setDidMount] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [morePages, setMorePages] = useState(false);

  useEffect(() => {
    setDidMount(true);
    getMerchants();
  }, []);

  useEffect(() => {
    if (didMount) {
      // changing sort or merchant filter resets page and previous data fetched
      setPages(1);
      setData([]);
    }
  }, [sortOption, merchants]);

  useEffect(() => {
    if (didMount) {
      setLoading(true);
      getData();
    }
  }, [pages, sortOption, merchants]);

  useEffect(() => {
    if (loadingProducts) {
      getProductsData();
    }
  }, [loadingProducts]);

  useEffect(() => {
    if (showMocks) {
      setData(mockData.attributed_checkouts);
    }
  }, [showMocks]);

  async function getData() {
    const selectedMerchants = merchants.reduce(
      (merchs, merch) => (merch.selected ? merchs.concat([merch.value]) : merchs),
      [],
    );

    const params = {
      page: pages,
      per_page: perPage,
      order_by: 'datetime_attributed_checkout',
      direction: sortOption.value,
    };

    const selectedMerchStr = selectedMerchants.join(',');

    if (selectedMerchStr) {
      params.merch_ids = selectedMerchStr;
    }

    try {
      const res = await ordersService.getData(match.params.publisherId, params);
      const newData = res.data.data[0].attributed_checkouts;
      setMorePages(res.data.data[0].page_info.next_page);

      const allData = pages > 1 ? data.concat(newData) : newData;

      setData(allData);
      setLoadingProducts(true);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }

  async function getProductsData() {
    const promises = [];
    const firstIndex = (pages - 1) * perPage;
    const lastIndex = pages * perPage;

    data.slice(firstIndex, lastIndex).forEach(order => {
      const params = {
        checkout_date: order.datetime_attributed_checkout_utc,
        event_id: order.event_id,
      };

      const promise = ordersService.getProductsData(match.params.publisherId, params);
      promises.push(promise);
    });

    const newData = cloneDeep(data);

    const responses = await Promise.all(promises);

    responses.forEach((res, index) => {
      const eventProducts = res.data.data[0].products;
      newData[index + firstIndex].event_products = eventProducts;
    });

    setData(newData);
    setLoadingProducts(false);
  }

  async function getMerchants() {
    const res = await ordersService.getMerchants(match.params.publisherId);
    const data = res.data.data[0];

    const merchants = data.merchants.map(merchant => ({
      label: merchant.full_name,
      value: merchant.merch_id,
      selected: true,
    }));
    setMerchants(merchants);
  }

  function getLabel(items) {
    const selected = items.reduce((acc, option) => {
      return option.selected ? [...acc, option.label] : acc;
    }, []);

    const oneselected = selected.length === 1;
    const allSelected = !selected.length || selected.length === items.length;
    return `Fulfilled By ${
      oneselected ? selected[0] : allSelected ? '' : `${selected.length} Merchants`
    }`;
  }

  const cards = data.map((data, index) => (
    <Card data={data} key={index} loadingProducts={loadingProducts} />
  ));

  const merchantDropdown = merchants.length ? (
    <ConfirmDropdown
      items={merchants}
      updateItems={setMerchants}
      itemType="merchants"
      labelOverride={getLabel}
      hasSearch={true}
      confirmLabel="Done"
      defaultSelectNone={true}
      defaultHideDeselectAll={true}
    />
  ) : null;

  const loadMore =
    data.length && morePages && !loading && !loadingProducts ? (
      <button className="load-more" onClick={() => setPages(pages + 1)}>
        Load More
      </button>
    ) : null;

  return (
    <div className="orders-container">
      <div className="top-items">
        <h1>Orders</h1>
        <div className="dropdowns">
          {merchantDropdown}
          <BasicDropdown
            items={sortOptions}
            selectItem={idx => setSortOption(sortOptions[idx])}
            dropdownLabel={`Sort By: ${sortOption.label}`}
          />
        </div>
      </div>
      <MocksButton setShowMocks={setShowMocks} showMocks={showMocks} />
      <div className="content">
        {cards}
        <Loader loading={loading} />
      </div>
      {loadMore}
      <NoResult type="orders-page" noResult={!loading && !data.length} />
    </div>
  );
};

export default Orders;
